import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { PostHogProvider } from 'posthog-js/react'
import * as Sentry from '@sentry/react'
import './index.css'
import AppRouter from './components/AppRouter'
import { handleDynamicImportFailure } from './utils/dynamicImportRecovery'
import { runtimeConfig } from './lib/runtimeConfig'

const reportModuleLoadFailure = (event: Event) => {
  const target = event.target
  if (!(target instanceof HTMLScriptElement)) return
  const isModule = target.type === 'module'
  if (!isModule) return

  Sentry.withScope((scope) => {
    scope.setTag('asset_type', 'module_script')
    scope.setContext('module_script', {
      src: target.src || null,
      currentSrc: target.currentSrc || null,
      async: target.async,
      defer: target.defer,
      crossOrigin: target.crossOrigin || null,
    })
    Sentry.captureMessage('Module script failed to load', 'error')
  })
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Avoid collecting default PII by default (can be enabled intentionally later)
    sendDefaultPii: false,
    beforeSend: (event) => {
      const exception = event.exception?.values?.[0]
      const isAbortError = exception?.type === 'AbortError'
      const isAbortMessage =
        exception?.value === 'signal is aborted without reason'
      const hasSupabaseLockFrame = exception?.stacktrace?.frames?.some(
        (frame) =>
          frame.filename?.includes('@supabase/auth-js/dist/module/lib/locks.js')
      )

      if (isAbortError && isAbortMessage && hasSupabaseLockFrame) {
        return null
      }

      return event
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay (Phase 1 MVP): strict privacy + only record sessions when an error occurs.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      // User Feedback (Phase 1 MVP): enabled, but manually opened from in-app UI.
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'system',
        enableScreenshot: true,
        // Match Printboard design system colors (tailwind.config.js)
        themeLight: {
          background: '#FAF9F6', // background-50
          foreground: '#3D3B36', // ink-700
          accentBackground: '#3D3B36', // primary-400
          accentForeground: '#FFFFFF',
          successColor: '#3BB28C', // state-success
          errorColor: '#E57373', // state-error
          outline: '2px solid rgba(61, 59, 54, 0.35)',
          boxShadow: '0px 8px 32px 0px rgba(61, 59, 54, 0.18)',
        },
        themeDark: {
          background: '#24221E', // primary-500
          foreground: '#FAF9F6', // background-50
          accentBackground: '#7BB3A5', // accent-400
          accentForeground: '#3D3B36', // ink-700
          successColor: '#3BB28C', // state-success
          errorColor: '#E57373', // state-error
          outline: '2px solid rgba(123, 179, 165, 0.45)',
          boxShadow: '0px 10px 40px 0px rgba(0, 0, 0, 0.35)',
        },
        formTitle: 'Report a bug',
        submitButtonLabel: 'Send report',
        messagePlaceholder: 'What happened? What did you expect?',
      }),
    ],
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0
    ),
    // Replay sampling: do not record baseline sessions; record only when an error occurs.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })
}

window.addEventListener('error', (event) => {
  reportModuleLoadFailure(event)
  handleDynamicImportFailure(event.error || event.message, 'window.error')
})
window.addEventListener('unhandledrejection', (event) => {
  handleDynamicImportFailure(event.reason, 'window.unhandledrejection')
})

// Temporarily disable StrictMode to prevent double-mounting flashing behavior
// StrictMode causes components to mount, unmount, and remount in development
// which triggers our canvas service cleanup/re-initialization cycle
createRoot(document.getElementById('root')!).render(
  runtimeConfig.hasPosthogConfig ? (
    <PostHogProvider
      apiKey={import.meta.env.VITE_POSTHOG_KEY}
      options={{
        api_host:
          import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        capture_exceptions: true,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            password: true,
          },
          captureCanvas: {
            // Fabric.js renders the board into a <canvas>; enable pixel capture so the board
            // is visible in PostHog session replays.
            // NOTE: Canvas pixel capture can add noticeable overhead while dragging/resizing.
            // Disable in development to keep the local UX responsive (HMR + large boards).
            recordCanvas: import.meta.env.MODE !== 'development',
            // Conservative defaults for performance + readability (can tune later).
            canvasFps: 4,
            canvasQuality: '0.4',
          },
        },
        debug: import.meta.env.MODE === 'development',
      }}
    >
      <HelmetProvider>
        <AppRouter />
      </HelmetProvider>
    </PostHogProvider>
  ) : (
    <HelmetProvider>
      <AppRouter />
    </HelmetProvider>
  )
)
